import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { apiUrl } from './hooks/useOnboardingStatus';
import './styles.css';

// Fallback allowances if the backend omits plan.allowance_usd.
const PLAN_ALLOWANCE_USD = { Starter: 5.0, Pro: 20.0, Studio: 50.0 };

// Usage analytics sourced from the Cloud Run backend via /api/usage/dashboard/v2.
// This surface is for usage quantities, model rankings, and ESTIMATED usage value
// only — Stripe (rendered by SubscriptionInfo) remains the source of truth for
// invoices, discounts, credits, and the actual amount due.
function UsageDashboard() {
  const { getAccessTokenSilently } = useAuth0();
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);
  const [usageError, setUsageError] = useState(null);
  const [rankingMode, setRankingMode] = useState('usage');

  useEffect(() => {
    let isMounted = true;
    const fetchUsageDashboard = async () => {
      try {
        setUsageLoading(true);
        setUsageError(null);
        const accessToken = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUTH0_AUDIENCE,
          },
        });
        const resp = await fetch(apiUrl('/usage/dashboard/v2?period_days=30'), {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (resp.status === 404) {
          if (isMounted) {
            setUsage(null);
            setUsageError('Usage analytics are not available for this account yet.');
          }
          return;
        }
        if (!resp.ok) {
          throw new Error(`Usage fetch failed (${resp.status})`);
        }
        const json = await resp.json();
        if (isMounted) setUsage(json);
      } catch (err) {
        if (isMounted) {
          setUsage(null);
          setUsageError('Unable to load usage at the moment.');
        }
      } finally {
        if (isMounted) setUsageLoading(false);
      }
    };
    fetchUsageDashboard();
    return () => {
      isMounted = false;
    };
  }, [getAccessTokenSilently]);

  const friendlyNumber = (value, decimals = 2) =>
    Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

  const formatUsageQuantity = (bucket) => {
    const qty = Number(bucket.usage_quantity || 0);
    if (bucket.unit_type === 'tokens') {
      return `${friendlyNumber(qty / 1_000_000, 3)}M tokens used`;
    }
    if (bucket.unit_type === 'seconds') {
      return `${friendlyNumber(qty, 0)} seconds used`;
    }
    return `${friendlyNumber(qty, 0)} items used`;
  };

  const formatRemainingUnits = (bucket) => {
    if (bucket.remaining_units_estimate == null) return null;
    if (bucket.unit_type === 'tokens') {
      return `${friendlyNumber(bucket.remaining_units_estimate / 1_000_000, 3)}M est. remaining`;
    }
    return `${friendlyNumber(bucket.remaining_units_estimate, 0)} est. remaining`;
  };

  const formatModelUsage = (model) => {
    if (!model) return 'No model data yet';
    const quantity = Number(model.usage_quantity || 0);
    if (model.unit_type === 'tokens') {
      if (quantity >= 1_000_000) return `${friendlyNumber(quantity / 1_000_000, 2)}M tokens`;
      if (quantity >= 1_000) return `${friendlyNumber(quantity / 1_000, 1)}K tokens`;
      return `${friendlyNumber(quantity, 0)} tokens`;
    }
    if (model.unit_type === 'seconds') {
      return `${friendlyNumber(quantity, 0)} seconds`;
    }
    return `${friendlyNumber(quantity, 0)} ${quantity === 1 ? 'image' : 'images'}`;
  };

  const formatPeriodEdge = (value) => {
    if (!value) return '';
    return new Date(value * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const planAllowanceUsd =
    usage?.plan?.allowance_usd ?? PLAN_ALLOWANCE_USD[usage?.plan?.tier] ?? 0;
  const isComplimentary =
    usage?.billing_mode === 'complimentary'
    || usage?.billing?.mode === 'complimentary';

  const rawTotalPercentUsed = usage?.totals?.percent_used ?? 0;
  const totalPercentUsed = rawTotalPercentUsed > 1
    ? rawTotalPercentUsed / 100
    : rawTotalPercentUsed;
  const totalSpendUsd = usage?.totals?.spend_usd ?? 0;
  const overageUsdFromApi = usage?.totals?.overage_usd ?? 0;
  const remainingUsd =
    usage?.totals?.remaining_usd ?? Math.max(planAllowanceUsd - totalSpendUsd, 0);
  const stripeNote = usage?.sources?.stripe?.note;
  const stripeNotLinked = stripeNote === 'no_stripe_customer';
  const bucketData = usage?.buckets ?? [];
  const modelData = usage?.models ?? [];
  const insights = usage?.insights;
  const usageRankedModels = [...modelData].sort(
    (a, b) => b.usage_quantity - a.usage_quantity || b.spend_usd - a.spend_usd,
  );
  const spendRankedModels = [...modelData].sort(
    (a, b) => b.spend_usd - a.spend_usd || b.usage_quantity - a.usage_quantity,
  );
  const rankedModels = (
    rankingMode === 'usage' ? usageRankedModels : spendRankedModels
  ).slice(0, 6);
  const rankingMax = rankedModels.reduce(
    (max, model) => Math.max(
      max,
      rankingMode === 'usage' ? model.usage_quantity : model.spend_usd,
    ),
    0,
  );
  const mostUsedModel = modelData.find(
    (model) => model.key === insights?.most_used_key,
  ) || usageRankedModels[0];
  const highestSpendModel = modelData.find(
    (model) => model.key === insights?.highest_spend_key,
  ) || spendRankedModels[0];
  const periodLabel = usage?.period
    ? `${formatPeriodEdge(usage.period.since)} – ${formatPeriodEdge(usage.period.until)}`
    : 'Current billing period';
  const hasBucketUsage = bucketData.some((bucket) => Number(bucket.usage_quantity) > 0);
  const hasRecordedUsage =
    hasBucketUsage
    || totalSpendUsd > 0
    || Number(usage?.trial_budget?.committedMicroUsd || 0) > 0;

  return (
    <div className="usage-summary">
      <div className="usage-dashboard-heading">
        <div>
          <span className="usage-dashboard-kicker">Account analytics</span>
          <h3>Usage this period</h3>
          <span className="usage-period-label">{periodLabel}</span>
        </div>
        <span className="usage-ledger-pill">
          <span className="usage-ledger-dot" aria-hidden="true" />
          Estimated usage ledger
        </span>
      </div>
      {usageLoading ? (
        <div className="usage-row muted" role="status">Loading usage…</div>
      ) : usageError ? (
        <div className="usage-row error" role="alert">{usageError}</div>
      ) : !usage ? (
        <div className="usage-row muted">Usage data unavailable.</div>
      ) : stripeNotLinked && !hasRecordedUsage ? (
        <div className="usage-row muted">Stripe billing is not linked for this account.</div>
      ) : !hasRecordedUsage ? (
        <div className="usage-row muted">No metered usage yet.</div>
      ) : (
        <>
          <div className="usage-overview">
            <div className="usage-overview-row">
              <span className="usage-overview-primary">
                {isComplimentary
                  ? `Usage value $${friendlyNumber(totalSpendUsd)} · $0 due`
                  : `Used $${friendlyNumber(totalSpendUsd)} of $${friendlyNumber(planAllowanceUsd)}`}
              </span>
              <span className="usage-overview-secondary">
                {isComplimentary
                  ? 'Complimentary access'
                  : totalPercentUsed > 0
                  ? `${friendlyNumber(totalPercentUsed * 100, 1)}% of allowance`
                  : 'No usage logged'}
              </span>
            </div>
            <div
              className="usage-progress general"
              role="progressbar"
              aria-label="Plan allowance used"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(Math.min(totalPercentUsed, 1) * 100)}
              aria-valuetext={
                isComplimentary
                  ? `$${friendlyNumber(totalSpendUsd)} estimated usage value with $0 due`
                  : `${friendlyNumber(Math.min(totalPercentUsed, 1) * 100, 1)}% of allowance used`
              }
            >
              <div
                className={`usage-progress-bar ${totalPercentUsed >= 1 ? 'over' : ''}`}
                style={{ width: `${Math.min(totalPercentUsed, 1) * 100}%` }}
              />
            </div>
            <div className="usage-overview-meta">
              {(usage?.totals?.average_cost_per_request ?? 0) > 0 && (
                <span className="usage-badge avg"><span className="icon">Ø</span>Avg $/req: {friendlyNumber(usage?.totals?.average_cost_per_request ?? 0, 4)}</span>
              )}
              {usage?.totals?.median_cost_per_request != null && (usage?.totals?.median_cost_per_request ?? 0) > 0 && (
                <span className="usage-badge median"><span className="icon">~</span>Median $/req: {friendlyNumber(usage.totals.median_cost_per_request, 4)}</span>
              )}
              {isComplimentary ? (
                <span className="usage-badge">Complimentary · $0 due</span>
              ) : (
                <span className="usage-badge">Allowance: ${friendlyNumber(planAllowanceUsd)}</span>
              )}
              <span className="usage-badge">
                Est. {isComplimentary ? 'usage value' : 'spend'}: ${friendlyNumber(totalSpendUsd)}
              </span>
              {(usage?.totals?.reserved_usd ?? 0) > 0 && (
                <span className="usage-badge">
                  Pending: ${friendlyNumber(usage?.totals?.reserved_usd ?? 0)}
                </span>
              )}
              {!isComplimentary && overageUsdFromApi > 0 && (
                <span className="usage-badge over">Overages: ${friendlyNumber(overageUsdFromApi)}</span>
              )}
              {!isComplimentary && (
                <span className="usage-badge">Remaining: ${friendlyNumber(remainingUsd)}</span>
              )}
              {usage?.totals?.estimated_requests_text_only != null && (
                <span className="usage-badge text-only"><span className="icon">📝</span>Text-only est.: {usage.totals.estimated_requests_text_only} reqs</span>
              )}
              {usage?.totals?.estimated_requests_blended != null && (
                <span className="usage-badge blended"><span className="icon">🎨</span>With media est.: {usage.totals.estimated_requests_blended} reqs</span>
              )}
              {usage?.totals?.average_cost_per_request_agent != null && (
                <span className="usage-badge agent"><span className="icon">⚙️</span>Agent avg $/req: {friendlyNumber(usage.totals.average_cost_per_request_agent, 4)}</span>
              )}
              {usage?.totals?.average_cost_per_request_direct != null && (
                <span className="usage-badge direct"><span className="icon">➡️</span>Direct avg $/req: {friendlyNumber(usage.totals.average_cost_per_request_direct, 4)}</span>
              )}
              {usage?.totals?.estimated_requests_remaining_agent != null && (
                <span className="usage-badge agent"><span className="icon">⚙️</span>Agent est. left: {usage.totals.estimated_requests_remaining_agent}</span>
              )}
              {usage?.totals?.estimated_requests_remaining_direct != null && (
                <span className="usage-badge direct"><span className="icon">➡️</span>Direct est. left: {usage.totals.estimated_requests_remaining_direct}</span>
              )}
            </div>
          </div>
          <div className="usage-insight-grid">
            <article className="usage-insight-card is-featured">
              <span className="usage-insight-label">Most used model</span>
              <strong>{mostUsedModel?.label || 'Waiting for activity'}</strong>
              <span className="usage-insight-meta">
                {mostUsedModel
                  ? `${mostUsedModel.provider_label} · ${formatModelUsage(mostUsedModel)}`
                  : 'New model usage will appear here'}
              </span>
            </article>
            <article className="usage-insight-card">
              <span className="usage-insight-label">Highest usage cost</span>
              <strong>{highestSpendModel?.label || 'No estimate yet'}</strong>
              <span className="usage-insight-meta">
                {highestSpendModel
                  ? `$${friendlyNumber(highestSpendModel.spend_usd, highestSpendModel.spend_usd < 0.01 ? 4 : 2)} estimated`
                  : 'Cost attribution begins with model activity'}
              </span>
            </article>
            <article className="usage-insight-card">
              <span className="usage-insight-label">Recorded activity</span>
              <strong>{friendlyNumber(insights?.request_count || 0, 0)} requests</strong>
              <span className="usage-insight-meta">
                Across {friendlyNumber(insights?.model_count || modelData.length, 0)} model paths
              </span>
            </article>
          </div>
          {modelData.length > 0 ? (
            <section className="usage-model-panel" aria-labelledby="usage-model-ranking-title">
              <div className="usage-model-panel-header">
                <div>
                  <h4 id="usage-model-ranking-title">Model breakdown</h4>
                  <p>See where your usage and estimated cost are concentrated.</p>
                </div>
                <div className="usage-ranking-toggle" aria-label="Rank models by">
                  <button
                    type="button"
                    className={rankingMode === 'usage' ? 'active' : ''}
                    aria-pressed={rankingMode === 'usage'}
                    onClick={() => setRankingMode('usage')}
                  >
                    Most used
                  </button>
                  <button
                    type="button"
                    className={rankingMode === 'spend' ? 'active' : ''}
                    aria-pressed={rankingMode === 'spend'}
                    onClick={() => setRankingMode('spend')}
                  >
                    Highest cost
                  </button>
                </div>
              </div>
              <ol className="usage-model-list">
                {rankedModels.map((model, index) => {
                  const rankingValue = rankingMode === 'usage'
                    ? model.usage_quantity
                    : model.spend_usd;
                  const barPercent = rankingMax > 0
                    ? Math.min((rankingValue / rankingMax) * 100, 100)
                    : 0;
                  const valueLabel = rankingMode === 'usage'
                    ? formatModelUsage(model)
                    : `$${friendlyNumber(model.spend_usd, model.spend_usd < 0.01 ? 4 : 2)}`;
                  return (
                    <li className="usage-model-row" key={model.key}>
                      <span className="usage-model-rank" aria-hidden="true">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div className="usage-model-main">
                        <div className="usage-model-copy">
                          <div className="usage-model-name">
                            <span className={`usage-provider-mark provider-${model.provider}`}>
                              {model.provider_label}
                            </span>
                            <strong title={model.model}>{model.label}</strong>
                          </div>
                          <span className="usage-model-value">{valueLabel}</span>
                        </div>
                        <div
                          className="usage-model-track"
                          role="progressbar"
                          aria-label={`${model.label}, ${valueLabel}`}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={Math.round(barPercent)}
                          aria-valuetext={valueLabel}
                        >
                          <span style={{ width: `${barPercent > 0 ? Math.max(barPercent, 3) : 0}%` }} />
                        </div>
                        <div className="usage-model-meta">
                          <span>{formatModelUsage(model)}</span>
                          <span>{friendlyNumber(model.request_count, 0)} requests</span>
                          <span>${friendlyNumber(model.spend_usd, model.spend_usd < 0.01 ? 4 : 2)} est.</span>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
              <div className="usage-accuracy-note">
                <span>{insights?.note || 'Model costs are estimated from recorded usage.'}</span>
                {(insights?.coverage_percent || 0) > 0 && (
                  <span>{friendlyNumber(insights?.coverage_percent || 0, 0)}% of period value attributed</span>
                )}
              </div>
            </section>
          ) : (
            <div className="usage-model-empty">
              <strong>Model insights are warming up.</strong>
              <span>Your totals are tracked now; model rankings appear as new activity is recorded.</span>
            </div>
          )}
          <div className="usage-bucket-grid">
            {bucketData.map((bucket) => {
              const percent = Math.min(bucket.percent_of_plan ?? 0, 1);
              const remainingText = formatRemainingUnits(bucket);
              return (
                <div key={bucket.key} className="usage-bucket">
                  <div className="usage-bucket-header">
                    <span className="usage-label">{bucket.label}</span>
                    <span className="usage-spend">
                      ~${friendlyNumber(bucket.spend_usd)}
                    </span>
                  </div>
                  <div
                    className="usage-progress"
                    role="progressbar"
                    aria-label={`${bucket.label} allowance used`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(percent * 100)}
                    aria-valuetext={`${friendlyNumber((bucket.percent_of_plan ?? 0) * 100, 1)}% of allowance`}
                  >
                    <div
                      className={`usage-progress-bar ${percent >= 1 ? 'over' : ''}`}
                      style={{ width: `${percent * 100}%` }}
                    />
                  </div>
                  <div className="usage-metrics">
                    <span className="usage-primary">{formatUsageQuantity(bucket)}</span>
                    <span className="usage-secondary">
                      {friendlyNumber((bucket.percent_of_plan ?? 0) * 100, 1)}% of allowance
                    </span>
                    {remainingText && <span className="usage-secondary">{remainingText}</span>}
                    {bucket.estimated_requests_remaining != null && (
                      <span className="usage-secondary">
                        Est. requests left ≈ {bucket.estimated_requests_remaining}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default UsageDashboard;
